import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Play, Pencil, Copy, ChevronLeft, ChevronRight, X, Trash2, Download, Bookmark, Archive, RotateCcw, FileText } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import JaceLayout from "./JaceLayout";
import { JACE, TYPE_LABELS, STATUS_COLORS, mondayOf, addDays, isoDay, isoWeek, fmtWeekRange, WEEKDAY_SHORT, MONTH_NL, weekStatus, WEEK_STATUS_COLORS } from "./shared";

export default function JaceSessions() {
  const nav = useNavigate();
  const [sessions, setSessions] = useState(null);
  const [weekOffset, setWeekOffset] = useState(0); // 0 = huidige week
  const [view, setView] = useState("week"); // week | month
  const [coachFilter, setCoachFilter] = useState("ALL");
  const [preview, setPreview] = useState(null); // session object
  const [addDay, setAddDay] = useState(null); // iso date
  const [dupWeek, setDupWeek] = useState(false);
  const [me, setMe] = useState("");

  const load = () => api.get("/jace/sessions").then(({ data }) => setSessions(data.items)).catch(() => setSessions([]));
  const exportExcel = async () => {
    try {
      const res = await api.get("/jace/sessions-export.xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "jace-sessies.xlsx";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel-export gedownload");
    } catch { toast.error("Exporteren mislukt"); }
  };
  useEffect(() => {
    load();
    const token = localStorage.getItem("joh_admin_token");
    api.get("/auth/me", { headers: { Authorization: `Bearer ${token}` } }).then(({ data }) => setMe(data.name || data.email)).catch(() => {});
  }, []);

  const monday = addDays(mondayOf(new Date()), weekOffset * 7);
  const weekNum = isoWeek(monday);
  const todayIso = isoDay(new Date());

  const coaches = useMemo(() => [...new Set((sessions || []).map((s) => s.assigned_coach || s.programmed_by).filter(Boolean))], [sessions]);
  const visible = (sessions || []).filter((s) => {
    if (s.status === "ARCHIVED") return false;
    if (coachFilter === "MY") return [s.assigned_coach, s.programmed_by].includes(me);
    if (coachFilter !== "ALL") return [s.assigned_coach, s.programmed_by].includes(coachFilter);
    return true;
  });

  const weekSessions = visible.filter((s) => s.date && s.date >= isoDay(monday) && s.date <= isoDay(addDays(monday, 6)));
  const wStatus = weekStatus(weekSessions);
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = addDays(monday, i);
    return { date: isoDay(d), day: d.getDate(), sessions: weekSessions.filter((s) => s.date === isoDay(d)).sort((a, b) => (a.start_time || "").localeCompare(b.start_time || "")) };
  });

  // Month overview: weken die deze maand raken
  const monthWeeks = useMemo(() => {
    if (view !== "month") return [];
    const first = new Date(monday.getFullYear(), monday.getMonth(), 1);
    const last = new Date(monday.getFullYear(), monday.getMonth() + 1, 0);
    const weeks = [];
    let m = mondayOf(first);
    while (m <= last) {
      const wk = visible.filter((s) => s.date && s.date >= isoDay(m) && s.date <= isoDay(addDays(m, 6)));
      weeks.push({ monday: new Date(m), num: isoWeek(m), count: wk.length, status: weekStatus(wk) });
      m = addDays(m, 7);
    }
    return weeks;
  }, [view, monday, visible]); // eslint-disable-line

  return (
    <JaceLayout>
      <div data-testid="jace-sessions">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <div className="section-tag section-tag-dark mb-3">// SESSIONS — WEEK PLANNER</div>
            <h2 className="font-display uppercase text-4xl md:text-6xl">Week {weekNum}.</h2>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <select value={coachFilter} onChange={(e) => setCoachFilter(e.target.value)} className="editorial-input dark !w-auto" data-testid="jace-coach-filter">
              <option value="ALL" className="bg-[#0A0A0A]">ALL COACHES</option>
              <option value="MY" className="bg-[#0A0A0A]">MY SCHEDULE</option>
              {coaches.filter((c) => c !== me).map((c) => <option key={c} value={c} className="bg-[#0A0A0A]">{c}</option>)}
            </select>
            <button onClick={exportExcel} className="btn-ghost-dark inline-flex items-center gap-2" data-testid="jace-export-btn"><Download size={13}/> EXPORT EXCEL</button>
            <button onClick={() => setView(view === "templates" ? "week" : "templates")} className="btn-ghost-dark inline-flex items-center gap-2" data-testid="jace-templates-tab"><Bookmark size={13}/> TEMPLATES</button>
            <button onClick={() => setDupWeek(true)} className="btn-ghost-dark inline-flex items-center gap-2" data-testid="jace-dup-week-btn"><Copy size={13}/> DUPLICATE WEEK</button>
            <button onClick={() => setView(view === "week" ? "month" : "week")} className="btn-ghost-dark" data-testid="jace-view-toggle">{view === "week" ? "MONTH" : "WEEK"}</button>
          </div>
        </div>

        {/* Weeknavigatie */}
        <div className="flex items-center gap-3 mb-6 flex-wrap" data-testid="jace-week-nav">
          <button onClick={() => setWeekOffset((o) => o - 1)} className="chip border border-[#F5F3F0]/25 px-3 py-2 inline-flex items-center gap-1 hover:bg-[#F5F3F0] hover:text-[#0A0A0A] transition-colors" data-testid="jace-prev-week"><ChevronLeft size={13}/> PREV WEEK</button>
          <span className="chip text-[#F5F3F0] px-2" data-testid="jace-week-label">WEEK {weekNum} · {fmtWeekRange(monday)}</span>
          <span className="chip px-2 py-1 border" style={{ color: WEEK_STATUS_COLORS[wStatus], borderColor: `${WEEK_STATUS_COLORS[wStatus]}55` }} data-testid="jace-week-status">{wStatus}{wStatus === "READY" || wStatus === "COMPLETED" ? " ✓" : ""}</span>
          <button onClick={() => setWeekOffset((o) => o + 1)} className="chip border border-[#F5F3F0]/25 px-3 py-2 inline-flex items-center gap-1 hover:bg-[#F5F3F0] hover:text-[#0A0A0A] transition-colors" data-testid="jace-next-week">NEXT WEEK <ChevronRight size={13}/></button>
          {weekOffset !== 0 && <button onClick={() => setWeekOffset(0)} className="chip px-3 py-2" style={{ color: JACE }} data-testid="jace-today-btn">TODAY</button>}
        </div>

        {sessions === null ? (
          <div className="chip text-[#A3A3A3]">Laden…</div>
        ) : view === "templates" ? (
          <TemplatesView />
        ) : view === "month" ? (
          /* MONTH OVERVIEW */
          <div data-testid="jace-month-view">
            <div className="chip text-[#A3A3A3] mb-4">{MONTH_NL[monday.getMonth()]} {monday.getFullYear()}</div>
            <div className="line-t line-dark">
              {monthWeeks.map((w) => (
                <button key={w.num} onClick={() => { setWeekOffset(Math.round((w.monday - mondayOf(new Date())) / (7 * 86400000))); setView("week"); }}
                  className="w-full text-left line-b line-dark py-4 flex items-center gap-6 hover:bg-white/[0.03] transition-colors" data-testid={`jace-month-week-${w.num}`}>
                  <span className="chip w-16" style={{ color: JACE }}>W{w.num}</span>
                  <span className="flex-1 text-sm text-[#DDD6CB]">{w.count} Sessions</span>
                  <span className="chip" style={{ color: WEEK_STATUS_COLORS[w.status] }}>{w.status}{["READY", "COMPLETED"].includes(w.status) ? " ✓" : w.status === "IN PROGRESS" ? " ●" : " ○"}</span>
                </button>
              ))}
            </div>
          </div>
        ) : (
          /* WEEK CALENDAR */
          <div className="grid grid-cols-1 md:grid-cols-7 gap-2" data-testid="jace-week-calendar">
            {days.map((d, i) => {
              const isToday = d.date === todayIso;
              return (
                <div key={d.date} className={`border ${isToday ? "border-[#C6FF3C]/40" : "border-[#F5F3F0]/10"} p-3 min-h-40 flex flex-col`} data-testid={`jace-cal-${d.date}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="chip" style={{ color: isToday ? JACE : "#A3A3A3" }}>{WEEKDAY_SHORT[i]} {String(d.day).padStart(2, "0")}</span>
                  </div>
                  <div className="flex-1 space-y-2">
                    {d.sessions.length === 0 && <div className="chip text-[#4A4A4A]">No sessions</div>}
                    {d.sessions.map((s) => (
                      <button key={s.session_id} onClick={() => setPreview(s)} className="w-full text-left border border-[#F5F3F0]/15 p-2.5 hover:border-[#C6FF3C]/60 transition-colors" data-testid={`jace-cal-session-${s.session_id}`}>
                        <div className="chip" style={{ color: JACE }}>{s.start_time || "—"}</div>
                        <div className="font-display uppercase text-sm leading-tight mt-0.5 truncate">{s.title || "—"}</div>
                        <div className="chip text-[#4A4A4A] mt-0.5 truncate">{TYPE_LABELS[s.type]}</div>
                        <div className="chip mt-1" style={{ color: STATUS_COLORS[s.status] }}>[{s.status}]</div>
                      </button>
                    ))}
                  </div>
                  <button onClick={() => setAddDay(d.date)} className="chip mt-2 border border-dashed border-[#F5F3F0]/25 px-2 py-2 text-[#A3A3A3] hover:border-[#C6FF3C] hover:text-[#C6FF3C] transition-colors w-full" data-testid={`jace-add-${d.date}`}>
                    + ADD SESSION
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Type kiezen voor nieuwe sessie op een dag */}
        {addDay && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setAddDay(null)}>
            <div className="bg-[#0A0A0A] border border-[#F5F3F0]/20 max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="jace-add-modal">
              <div className="chip text-[#A3A3A3] mb-1">// NEW SESSION · {addDay}</div>
              <div className="font-display uppercase text-2xl mb-5">Select type.</div>
              <div className="space-y-2">
                {Object.entries(TYPE_LABELS).map(([k, l]) => (
                  <button key={k} onClick={() => nav(`/jace/sessions/new?type=${k}&date=${addDay}`)}
                    className="w-full text-left border border-[#F5F3F0]/15 px-4 py-3 chip hover:border-[#C6FF3C] hover:text-[#C6FF3C] transition-colors"
                    data-testid={`jace-add-type-${k}`}>{l}</button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Session preview vanuit kalender */}
        {preview && <SessionPreview s={preview} onClose={() => setPreview(null)} onChanged={() => { setPreview(null); load(); }} />}

        {/* Duplicate week */}
        {dupWeek && <DuplicateWeekModal monday={monday} weekNum={weekNum} weekSessions={weekSessions} onClose={() => setDupWeek(false)} onDone={() => { setDupWeek(false); load(); }} />}
      </div>
    </JaceLayout>
  );
}

function TemplatesView() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/jace/templates").then(({ data }) => setItems(data.items || [])).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const setTheme = async (t, theme) => {
    try {
      await api.put(`/jace/templates/${t.id}`, { theme });
      toast.success("Thema opgeslagen");
      load();
    } catch { toast.error("Opslaan mislukt"); }
  };
  const toggleArchive = async (t, archived) => {
    if (archived && !window.confirm(`Template "${t.name}" archiveren? Je kunt hem later herstellen.`)) return;
    try {
      await api.post(`/jace/templates/${t.id}/archive`, { archived });
      load();
    } catch { toast.error("Mislukt"); }
  };

  if (items === null) return <div className="chip text-[#A3A3A3]">Laden…</div>;
  const active = items.filter((t) => !t.archived);
  const archived = items.filter((t) => t.archived);

  return (
    <div data-testid="jace-templates-view">
      <div className="chip text-[#A3A3A3] mb-2">// LES-TEMPLATES ({active.length} actief)</div>
      <div className="line-t line-dark">
        {active.length === 0 && <div className="chip text-[#4A4A4A] py-6">Nog geen templates. Maak er een vanuit een sessie ("SAVE AS TEMPLATE").</div>}
        {active.map((t) => <TemplateRow key={t.id} t={t} isArchived={false} onSetTheme={setTheme} onToggleArchive={toggleArchive} />)}
      </div>
      <div className="chip text-[#4A4A4A] mt-10 mb-2">// Gearchiveerd ({archived.length})</div>
      <div className="line-t line-dark opacity-70" data-testid="jace-templates-archived">
        {archived.length === 0 && <div className="chip text-[#4A4A4A] py-4">Geen gearchiveerde templates.</div>}
        {archived.map((t) => <TemplateRow key={t.id} t={t} isArchived onSetTheme={setTheme} onToggleArchive={toggleArchive} />)}
      </div>
    </div>
  );
}

// Module-scope (niet inline) zodat het thema-veld zijn focus niet verliest bij re-renders.
function TemplateRow({ t, isArchived, onSetTheme, onToggleArchive }) {
  const nav = useNavigate();
  return (
    <div className="line-b line-dark py-4 flex flex-wrap items-center gap-3" data-testid={`jace-template-row-${t.id}`}>
      <div className="flex-1 min-w-40">
        <div className="font-display uppercase text-lg">{t.name}</div>
        <div className="chip text-[#4A4A4A]">{TYPE_LABELS[t.type]} · {t.version} · {t.planned_duration || 60} MIN</div>
      </div>
      <input defaultValue={t.theme || ""} placeholder="Thema (bv. Defense week)" onBlur={(e) => { if ((e.target.value || "") !== (t.theme || "")) onSetTheme(t, e.target.value); }}
        className="editorial-input dark !w-56" data-testid={`jace-template-theme-${t.id}`} />
      {!isArchived && (
        <button onClick={() => nav(`/jace/sessions/new?template=${t.id}`)} className="chip px-3 py-2 border inline-flex items-center gap-1.5 hover:bg-[#F5F3F0] hover:text-[#0A0A0A] transition-colors" style={{ borderColor: JACE, color: JACE }} data-testid={`jace-template-use-${t.id}`}>
          <Plus size={12}/> GEBRUIK IN NIEUWE LES
        </button>
      )}
      <button onClick={() => onToggleArchive(t, !isArchived)} className="chip px-3 py-2 border border-[#F5F3F0]/20 text-[#A3A3A3] hover:text-[#F5F3F0] inline-flex items-center gap-1.5" data-testid={`jace-template-archive-${t.id}`}>
        {isArchived ? <><RotateCcw size={12}/> HERSTEL</> : <><Archive size={12}/> ARCHIVEER</>}
      </button>
    </div>
  );
}

function SessionPreview({ s, onClose, onChanged }) {
  const nav = useNavigate();
  const [dupDay, setDupDay] = useState(false);
  const [target, setTarget] = useState("");

  const dupToDay = async () => {
    if (!target) { toast.error("Kies een datum"); return; }
    try {
      const { data } = await api.post(`/jace/sessions/${s.session_id}/duplicate`, { date: target });
      toast.success(`Gedupliceerd als ${data.session.session_id}`);
      onChanged();
    } catch { toast.error("Dupliceren mislukt"); }
  };

  const activate = async () => {
    try {
      await api.post(`/jace/sessions/${s.session_id}/status`, { status: "PLANNED" });
      toast.success(`Sessie ${s.session_id} geactiveerd (PLANNED)`);
      onChanged();
    } catch (err) { toast.error(err?.response?.data?.detail || "Activeren mislukt"); }
  };

  const remove = async () => {
    if (!window.confirm(`Sessie "${s.title || s.session_id}" definitief verwijderen?\n\nDit kan niet ongedaan worden gemaakt. Wil je hem bewaren, gebruik dan archiveren.`)) return;
    try {
      await api.delete(`/jace/sessions/${s.session_id}`);
      toast.success("Sessie verwijderd");
      onChanged();
    } catch { toast.error("Verwijderen mislukt"); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0A0A0A] border border-[#F5F3F0]/20 max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="jace-session-preview">
        <div className="flex items-start justify-between">
          <div>
            <div className="chip" style={{ color: JACE }}>{s.session_id}</div>
            <div className="font-display uppercase text-3xl mt-1">{s.title || "—"}</div>
            <div className="chip text-[#A3A3A3] mt-2">{TYPE_LABELS[s.type]} · {s.date} {s.start_time} · {s.planned_duration} MIN</div>
            <div className="chip text-[#A3A3A3] mt-1">{s.assigned_coach ? `COACH: ${s.assigned_coach.toUpperCase()}` : "GEEN COACH"} · <span style={{ color: STATUS_COLORS[s.status] }}>{s.status}</span></div>
          </div>
          <button onClick={onClose} className="w-8 h-8 border border-[#F5F3F0]/25 flex items-center justify-center"><X size={14}/></button>
        </div>
        {(s.objectives || []).length > 0 && (
          <div className="mt-4">
            <div className="chip text-[#4A4A4A] mb-1">OBJECTIVES</div>
            {s.objectives.map((o, i) => <div key={i} className="text-sm text-[#DDD6CB] py-0.5">{String(i + 1).padStart(2, "0")} — {o.title}</div>)}
          </div>
        )}
        <div className="flex flex-wrap gap-2 mt-6">
          <Link to={`/jace/sessions/${s.session_id}`} className="px-4 py-2.5 chip" style={{ background: JACE, color: "#0A0A0A" }} data-testid="jace-preview-open">OPEN SESSION</Link>
          <Link to={`/jace/sessions/${s.session_id}/edit`} className="chip px-4 py-2.5 border border-[#F5F3F0]/25 inline-flex items-center gap-1.5 hover:bg-[#F5F3F0] hover:text-[#0A0A0A] transition-colors" data-testid="jace-preview-edit"><Pencil size={12}/> EDIT</Link>
          <button onClick={() => setDupDay(!dupDay)} className="chip px-4 py-2.5 border border-[#F5F3F0]/25 inline-flex items-center gap-1.5 hover:bg-[#F5F3F0] hover:text-[#0A0A0A] transition-colors" data-testid="jace-preview-dup"><Copy size={12}/> DUPLICATE TO DAY</button>
          {s.status === "DRAFT" && (
            <button onClick={activate} className="chip px-4 py-2.5 border inline-flex items-center gap-1.5 transition-colors" style={{ borderColor: "#4ADE80", color: "#4ADE80" }} data-testid="jace-preview-activate"><Play size={12}/> ACTIVEER (DRAFT → PLANNED)</button>
          )}
          {(s.status === "READY" || s.status === "IN PROGRESS") && (
            <button onClick={() => nav(`/jace/play/${s.session_id}`)} className="chip px-4 py-2.5 inline-flex items-center gap-1.5" style={{ background: JACE, color: "#0A0A0A" }} data-testid="jace-preview-play"><Play size={12}/> PLAY</button>
          )}
          {["COMPLETED", "REVIEWED"].includes(s.status) && (
            <button onClick={() => nav(`/jace/feedback/new?session=${s.session_id}`)} className="chip px-4 py-2.5 border inline-flex items-center gap-1.5 transition-colors" style={{ borderColor: JACE, color: JACE }} data-testid="jace-preview-feedback"><FileText size={12}/> FEEDBACK</button>
          )}
          <button onClick={remove} className="chip px-4 py-2.5 border border-[#F87171]/50 text-[#F87171] inline-flex items-center gap-1.5 hover:bg-[#F87171] hover:text-[#0A0A0A] transition-colors" data-testid="jace-preview-delete"><Trash2 size={12}/> VERWIJDER</button>
        </div>
        {dupDay && (
          <div className="flex gap-2 mt-4 items-end">
            <div className="flex-1"><label className="chip text-[#A3A3A3]">Naar datum</label><input type="date" value={target} onChange={(e) => setTarget(e.target.value)} className="editorial-input dark" data-testid="jace-dup-date" /></div>
            <button onClick={dupToDay} className="chip px-4 py-2.5 border" style={{ borderColor: JACE, color: JACE }} data-testid="jace-dup-confirm">Kopieer</button>
          </div>
        )}
      </div>
    </div>
  );
}

function DuplicateWeekModal({ monday, weekNum, weekSessions, onClose, onDone }) {
  const [targetOffset, setTargetOffset] = useState(1);
  const [mode, setMode] = useState("full");
  const [selected, setSelected] = useState(weekSessions.map((s) => s.session_id));
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (selected.length === 0) { toast.error("Selecteer minimaal één sessie"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/jace/sessions/duplicate-week", {
        from_monday: isoDay(monday),
        to_monday: isoDay(addDays(monday, targetOffset * 7)),
        mode,
        session_ids: selected,
      });
      toast.success(`Week gekopieerd — ${data.count} nieuwe sessies`);
      onDone();
    } catch (err) { toast.error(err?.response?.data?.detail || "Kopiëren mislukt"); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0A0A0A] border border-[#F5F3F0]/20 max-w-md w-full p-6 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="jace-dupweek-modal">
        <div className="chip text-[#A3A3A3] mb-1">// DUPLICATE WEEK {weekNum}</div>
        <div className="font-display uppercase text-2xl mb-5">Kopieer week.</div>
        <label className="chip text-[#A3A3A3]">Naar week</label>
        <select value={targetOffset} onChange={(e) => setTargetOffset(parseInt(e.target.value))} className="editorial-input dark mb-4" data-testid="jace-dupweek-target">
          {[1, 2, 3, 4].map((o) => <option key={o} value={o} className="bg-[#0A0A0A]">WEEK {isoWeek(addDays(monday, o * 7))} ({fmtWeekRange(addDays(monday, o * 7))})</option>)}
        </select>
        <label className="chip text-[#A3A3A3]">Modus</label>
        <div className="flex gap-2 mb-4">
          {[["full", "COPY FULL SESSIONS"], ["structure", "COPY STRUCTURE ONLY"]].map(([v, l]) => (
            <button key={v} onClick={() => setMode(v)} className="chip px-3 py-2 border transition-colors" style={mode === v ? { borderColor: JACE, color: JACE } : { borderColor: "#F5F3F033", color: "#A3A3A3" }} data-testid={`jace-dupweek-${v}`}>{l}</button>
          ))}
        </div>
        <label className="chip text-[#A3A3A3]">Sessies ({selected.length}/{weekSessions.length})</label>
        <div className="space-y-1 mb-5 mt-1">
          {weekSessions.length === 0 && <div className="chip text-[#4A4A4A] py-2">Geen sessies in deze week.</div>}
          {weekSessions.map((s) => (
            <label key={s.session_id} className="flex items-center gap-3 py-1.5 text-sm text-[#DDD6CB] cursor-pointer" data-testid={`jace-dupweek-sel-${s.session_id}`}>
              <input type="checkbox" checked={selected.includes(s.session_id)} onChange={(e) => setSelected(e.target.checked ? [...selected, s.session_id] : selected.filter((x) => x !== s.session_id))} className="accent-[#C6FF3C]" />
              <span className="chip text-[#4A4A4A] w-14">{s.start_time || "—"}</span> {s.title}
            </label>
          ))}
        </div>
        <div className="flex gap-3">
          <button onClick={submit} disabled={busy} className="flex-1 py-3 chip" style={{ background: JACE, color: "#0A0A0A" }} data-testid="jace-dupweek-submit">{busy ? "Kopiëren…" : "DUPLICATE WEEK"}</button>
          <button onClick={onClose} className="btn-ghost-dark">Annuleren</button>
        </div>
      </div>
    </div>
  );
}
